import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthSheet from "@/components/AuthSheet";
import { useToast } from "@/components/Toasts";

const AuthSheetCtx = createContext(null);

/**
 * Owns the one sign-in sheet for the whole storefront.
 *
 * Sign-in is offered from places that sit at very different depths — the home
 * header, the prompt inside the confirmation screen, the profile — and all of
 * them interrupt an order in progress rather than navigate away. A tiny context
 * beats threading an `onSignIn` callback through every screen in between.
 */
export const AuthSheetProvider = ({ children }) => {
  const [mode, setMode] = useState(null); // null = closed | "signin" | "register"
  const { toast } = useToast();
  const { t } = useTranslation();

  const openSignIn = useCallback(() => setMode("signin"), []);
  const openRegister = useCallback(() => setMode("register"), []);
  const closeAuth = useCallback(() => setMode(null), []);
  // Success closes the sheet, and the toast says so — the sheet vanishing
  // alone reads as "dismissed", not as "you are in".
  const finishAuth = useCallback(() => {
    setMode(null);
    toast(t("toast.signedIn"));
  }, [toast, t]);

  const value = useMemo(
    () => ({ openSignIn, openRegister, closeAuth, isAuthOpen: mode !== null }),
    [openSignIn, openRegister, closeAuth, mode]
  );

  return (
    <AuthSheetCtx.Provider value={value}>
      {children}
      {mode && (
        // Signing in sets `user`, which is what App.js waits for before attaching
        // a pending order — closing the sheet is all that is left to do here.
        <AuthSheet initialMode={mode} onClose={closeAuth} onSuccess={finishAuth} />
      )}
    </AuthSheetCtx.Provider>
  );
};

export const useAuthSheet = () => {
  const ctx = useContext(AuthSheetCtx);
  if (!ctx) throw new Error("useAuthSheet must be inside AuthSheetProvider");
  return ctx;
};
